"use client";

import { useEffect, useState } from "react";
import { paymentFailureMessage } from "@/lib/payments/payment-failure";
import { checkoutEvent } from "./checkout-telemetry";
import Link from "next/link";
import { PaymentElement, ExpressCheckoutElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useSearchParams } from "next/navigation";
import { Loader2, Lock } from "lucide-react";
export default function CheckoutForm({
  amountStr,
  returnUrl,
  isSetupIntent = false,
  compact = false
}: {
  amountStr: string;
  returnUrl: string;
  isSetupIntent?: boolean;
  compact?: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState<string | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasExpressPayment, setHasExpressPayment] = useState(true);
  const searchParams = useSearchParams();
  useEffect(() => {
    if (!stripe) {
      return;
    }
    const clientSecretParam = searchParams.get(isSetupIntent ? "setup_intent_client_secret" : "payment_intent_client_secret");
    if (!clientSecretParam) {
      return;
    }
    const retrieveFn = isSetupIntent ? stripe.retrieveSetupIntent : stripe.retrievePaymentIntent;
    retrieveFn(clientSecretParam).then(res => {
      const intent = "setupIntent" in res ? res.setupIntent : "paymentIntent" in res ? res.paymentIntent : undefined;
      if (!intent) return;
      switch (intent.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe, isSetupIntent, searchParams]);
  const confirm = async (method: "card" | "express") => {
    if (!stripe || !elements || isLoading || uncertain) return;
    setIsLoading(true);
    setMessage(null);
    const diagnostics = {
      provider: "stripe" as const,
      payment_method: method,
      attempt_id: crypto.randomUUID()
    };
    checkoutEvent("checkout_confirmation_started", {
      ...diagnostics,
      outcome: "started"
    });
    try {
      const confirmFn = isSetupIntent ? stripe.confirmSetup : stripe.confirmPayment;
      const {
        error
      } = await confirmFn({
        elements,
        confirmParams: {
          return_url: returnUrl
        }
      });
      if (error) {
        checkoutEvent("checkout_confirmation_failed", {
          ...diagnostics,
          outcome: "failed",
          reason: error.type,
          error_code: error.code
        });
        setMessage(paymentFailureMessage(error));
        if (!["card_error", "validation_error"].includes(error.type)) setUncertain(true);
      }
    } catch {
      setUncertain(true);
      checkoutEvent("checkout_confirmation_failed", {
        ...diagnostics,
        outcome: "uncertain",
        reason: "connection_interrupted"
      });
      setMessage("Connection interrupted. Check your payment status before trying again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await confirm("card");
  };
  const onConfirm = async () => {
    await confirm("express");
  };
  return <form className="space-y-6" onSubmit={handleSubmit}>
      <div hidden={!hasExpressPayment} inert={uncertain || isLoading}>
      <div className="space-y-4">
        {!compact && <p className="text-sm font-semibold text-foreground">Express Checkout</p>}
        <ExpressCheckoutElement onConfirm={onConfirm} options={compact ? {
          buttonHeight: 44,
          paymentMethods: {
            link: "auto",
            applePay: "never",
            googlePay: "never",
            amazonPay: "never",
            paypal: "never",
            klarna: "never"
          }
        } : undefined} onReady={({
          availablePaymentMethods
        }) => setHasExpressPayment(Boolean(availablePaymentMethods && Object.values(availablePaymentMethods).some(Boolean)))} onLoadError={() => {
          setHasExpressPayment(false);
          checkoutEvent("checkout_express_load_failed", {
            provider: "stripe",
            outcome: "unavailable"
          });
        }} />
      </div>

      <div className="relative flex items-center py-4">
        <div className="flex-grow border-t border-border"></div>
        <span className="flex-shrink mx-4 text-muted-foreground text-xs font-medium uppercase">Or pay with card</span>
        <div className="flex-grow border-t border-border"></div>
      </div>

      </div>
      <PaymentElement id="payment-element" onLoadError={() => checkoutEvent("checkout_card_load_failed", {
      provider: "stripe",
      outcome: "unavailable"
    })} options={compact ? {
      layout: {
        type: "accordion",
        radios: "never",
        defaultCollapsed: false,
        spacedAccordionItems: false
      },
      wallets: {
        applePay: "never",
        googlePay: "never",
        link: "never"
      }
    } : {
      layout: "tabs"
    }} />

      <div className="pt-4">
        <button className="w-full bg-primary text-primary-foreground py-4 rounded-lg font-bold text-lg shadow-lg shadow-primary/30 hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50" type="submit" disabled={isLoading || uncertain || !stripe || !elements}>
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
          Pay {amountStr}
        </button>
        
        {message && <div role="alert" className="text-red-500 text-sm mt-4 text-center">{message}</div>}

        <p className="text-center text-xs text-muted-foreground mt-4">
          By confirming your purchase, you agree to our <Link className="underline" href="/terms">Terms of Service</Link> and <Link className="underline" href="/privacy">Privacy Policy</Link>.
        </p>
      </div>
    </form>;
}